import OrderRuleStatusLabel from "@/features/line-sheet-sets/order-rule-status-label";
import {
  Box,
  Flex,
  FormLabel,
  Grid,
  Heading,
  Select,
  Spacer,
  Text,
} from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import ApplyConditionModal from "@/features/line-sheet-sets/apply-condition-modal";
import OrderRuleDraftBox from "@/features/line-sheet-sets/order-rule-draft-box";
import useUpdateLineSheetSetOrderRules from "@/features/line-sheet-sets/hooks/use-update-line-sheet-set-order-rules";
import AppLineSheetTypeSelector from "@/features/line-sheet-sets/app-line-sheet-type-selector";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { toLower } from "@/utils/case";
import AppSeasonSelect from "@/features/line-sheet-sets/app-season-select";
import AppCompanySelect from "@/features/line-sheet-sets/app-company-select";
import { Field, FieldProps, FormikProps } from "formik";
import TextareaAutosize from "react-textarea-autosize";
import AppDatePicker from "@/features/line-sheet-sets/app-date-picker";
import {
  AttachmentFormField,
  isLineSheetFormField,
  LineSheetSetFormFields,
} from "@/features/line-sheet-sets/detail/line-sheet-set-detail-page";
import { getFileNameWithoutExtension } from "@/features/types";
import useIdentity from "@/features/ui/hooks/use-identity";
import CommissionInlineEdit from "@/features/line-sheet-sets/detail/commission-inline-edit";
import ToBeSubmittedInlineEdit from "@/features/line-sheet-sets/detail/to-be-submitted-inline-edit";
import MoqInlineEdit from "@/features/line-sheet-sets/detail/moq-inline-edit";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import AppInput from "@/features/ui/app-input";
import AppListFormControl from "@/features/transportations/app-list-form-control";
import AppCurrencySelect from "../app-currency-select";
import AppSelect from "@/features/line-sheet-sets/app-select";
import SVG from "react-inlinesvg";
import useDownloadUnoFileInfinite from "@/hooks/use-download-uno-file-infinite";
import {
  findYWRInfiniteSegment,
  postProcessYWRInfiniteData,
} from "@/features/ui/helpers/ywr-helpers";
import useDownloadUnoFile, {
  downloadUnoFileKey,
} from "@/hooks/use-download-uno-file";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { fileIconsByType } from "@/shared/constants";
import CustomDatePicker from "@/shared/components/date-picker/CustomDatePicker";

interface LineSheetSetInformationFormControlProps {
  isEditing: boolean;
  onPublishClick: () => void;
  formikProps: FormikProps<LineSheetSetFormFields>;
  onConditionApplied?: () => void;
  // 제출처(세트 단위) 변경 후 서버 데이터 재검증(mutate) 트리거용.
  onOrderSheetSetUpdated?: () => void;
  // 카드 우상단(제목 오른쪽)에 붙는 버튼 — 예: 패널 접기
  headerAction?: React.ReactNode;
}

export default function LineSheetSetInformationFormControl({
  isEditing,
  formikProps,
  onPublishClick,
  onConditionApplied,
  onOrderSheetSetUpdated,
  headerAction,
}: LineSheetSetInformationFormControlProps) {
  const identity = useIdentity();
  const isAgency = identity?.company?.type === "AGENCY";
  const isBoutique = identity?.company?.type === "BOUTIQUE";
  const [isApplyConditionOpen, setIsApplyConditionOpen] = useState(false);

  const {
    t,
    tUpper,
    tTitle,
    tLocalDateString,
    tLocalDateTimeString2,
    tCurrencyString,
    tNumberString,
    tCountString,
    tConditionAppliedCurrencyString,
  } = useI18nHelper();

  const issuedToBuyersMeta = useMemo(() => {
    const issuedToBuyers = formikProps.values.issuedToBuyers || [];
    let name = "";
    if (issuedToBuyers.length > 0) {
      name = t(
        issuedToBuyers.length > 1
          ? "issued_to_buyers_others"
          : "issued_to_buyers_one",
        {
          first: issuedToBuyers[0].name,
          count: issuedToBuyers.length,
        }
      );
    }
    return name;
  }, [formikProps.values.issuedToBuyers, t]);

  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();

  // 오더룰 정제는 세트를 통째로 수정하는 일이 아니라 매일 반복되는 별도 작업이라, 적용이
  // '대기중'인 동안에는 폼 수정 모드와 무관하게 오더룰만 고치고 저장할 수 있게 한다.
  // 오더룰은 발행 뒤에도 고칠 수 있다(부티크·에이전시 모두, 서버도 허용). 세트의 다른 정보는
  // 발행 뒤 수정이 막혀 있어 폼 수정 모드로는 못 들어가므로, 보기 화면에서 수정 버튼을 눌러야
  // 입력칸이 열리고 버튼이 저장으로 바뀐다. 에이전시는 '대기중'이면 이미 입력칸이 열려 있어
  // 수정 버튼을 두지 않는다. 새로 올리는 중(id 없음)이나 폼 수정 모드에서는 폼 저장이
  // 오더룰까지 함께 저장한다. 삭제된 세트는 막는다.
  const isAgencyOrderRulePending =
    isAgency && formikProps.values.orderRuleStatus === "PENDING";
  const canEditOrderRuleInline =
    (isBoutique || (isAgency && !isAgencyOrderRulePending)) &&
    !isEditing &&
    !!formikProps.values.id &&
    !formikProps.values.isDeleted;
  const [isInlineOrderRuleEditing, setIsInlineOrderRuleEditing] =
    useState(false);
  const canEditOrderRule =
    (isAgency &&
      !!formikProps.values.id &&
      !formikProps.values.isDeleted &&
      (isEditing || isAgencyOrderRulePending)) ||
    (isBoutique && isEditing) ||
    (canEditOrderRuleInline && isInlineOrderRuleEditing);

  // 폼 수정 모드에서는 폼의 저장 버튼이 오더룰까지 함께 저장하므로 별도 저장 버튼을 두지 않는다.
  const showOrderRuleSaveButton = canEditOrderRule && !isEditing;

  // 초안 적용은 폼 수정 모드와 무관하게 바로 저장한다 — 매일 반복되는 작업이라
  // 수정 모드로 들어갔다 나오는 왕복을 없앤다. 발행된 세트도 된다(삭제된 세트만 막는다).
  const canSaveOrderRuleDirectly =
    isAgency && !!formikProps.values.id && !formikProps.values.isDeleted;

  const {
    fire: fireUpdateOrderRules,
    data: updateOrderRulesData,
    isLoading: isUpdateOrderRulesLoading,
    error: updateOrderRulesError,
    clear: clearUpdateOrderRules,
  } = useUpdateLineSheetSetOrderRules();

  const { error: showError } = useAppToasts();

  useEffect(() => {
    showError(updateOrderRulesError);
  }, [showError, updateOrderRulesError]);

  // 보기 화면에서 고칠 때는 입력을 폼이 아니라 여기에 담는다.
  // 상세 페이지는 enableReinitialize 인 데다 파싱/압축이 진행 중이면 5초마다 데이터를
  // 다시 읽어 폼을 통째로 초기화한다. 폼 필드에 바로 쓰면 타이핑이 그때마다 날아간다.
  // null = 아직 손대지 않음(= 서버 값을 그대로 보여준다).
  const [editedOrderRules, setEditedOrderRules] = useState<string | null>(null);

  const savedOrderRules = formikProps.values.joinedOrderRules ?? "";
  const orderRuleValue =
    showOrderRuleSaveButton && editedOrderRules !== null
      ? editedOrderRules
      : savedOrderRules;
  const isOrderRuleDirty =
    editedOrderRules !== null && editedOrderRules !== savedOrderRules;

  const handleChangeOrderRules = useCallback(
    (value: string) => {
      if (showOrderRuleSaveButton) {
        setEditedOrderRules(value);
      } else {
        formikProps.setFieldValue("joinedOrderRules", value);
      }
    },
    [showOrderRuleSaveButton, formikProps]
  );

  useEffect(() => {
    if (!updateOrderRulesData) {
      return;
    }
    // 저장이 끝나면 로컬 편집분을 버리고 다시 서버 값을 따라간다.
    // 서버가 공백을 정리해 저장하므로 화면도 그 결과로 맞춘다.
    const saved = (updateOrderRulesData.orderRules ?? []).join("\n");
    formikProps.setFieldValue("joinedOrderRules", saved);
    setEditedOrderRules(null);
    // 보기 화면에서 수정 버튼으로 고치던 중이었다면 저장과 함께 편집을 닫는다
    setIsInlineOrderRuleEditing(false);
    clearUpdateOrderRules();
    // 폼은 enableReinitialize 라서 서버 데이터를 다시 읽지 않으면 다음 초기화 때
    // 방금 저장한 오더룰이 예전 값으로 되돌아간다.
    onOrderSheetSetUpdated?.();
  }, [
    updateOrderRulesData,
    clearUpdateOrderRules,
    formikProps,
    onOrderSheetSetUpdated,
  ]);

  const handleSaveOrderRules = useCallback(() => {
    const companyId = identity?.company?.id;
    const lineSheetSetId = formikProps.values.id;
    if (!companyId || !lineSheetSetId || editedOrderRules === null) {
      return;
    }
    fireUpdateOrderRules(
      // 부티크는 부티크 API 로 저장한다(발행 뒤에도 허용)
      isBoutique
        ? { boutiqueId: companyId, lineSheetSetId }
        : { agencyId: companyId, lineSheetSetId },
      { orderRules: editedOrderRules.split("\n") }
    );
  }, [
    identity,
    isBoutique,
    formikProps.values.id,
    editedOrderRules,
    fireUpdateOrderRules,
  ]);

  const handleSaveOrderRuleDraft = useCallback(
    (text: string) => {
      const agencyId = identity?.company?.id;
      const lineSheetSetId = formikProps.values.id;
      if (!agencyId || !lineSheetSetId) {
        return;
      }
      fireUpdateOrderRules(
        { agencyId, lineSheetSetId },
        { orderRules: text.split("\n") }
      );
    },
    [identity, formikProps.values.id, fireUpdateOrderRules]
  );

  const orderRuleFiles = formikProps.values.lineSheetOrAttachmentList
    .filter((item): item is AttachmentFormField => !!item.isOrderRule)
    .map((item) => ({
      id: item.id,
      name: item.name,
    }));

  const lineSheetTargets = formikProps.values.lineSheetOrAttachmentList
    .filter(isLineSheetFormField)
    .filter((item) => item.type === "LINE_SHEET")
    .map((item) => ({
      id: item.id,
      name: item.name || getFileNameWithoutExtension(item.file),
    }));

  // LSS의 시즌 목록 — 라인시트별 seasons를 모아 unique. 시즌별 적용 탭 데이터.
  const seasonTargets = Array.from(
    new Set(
      formikProps.values.lineSheetOrAttachmentList
        .filter(isLineSheetFormField)
        .flatMap((item) => item.seasons ?? [])
        .filter((s): s is string => !!s)
    )
  );

  const attachmentFiles = formikProps.values.lineSheetOrAttachmentList
    .filter(
      (item): item is AttachmentFormField =>
        !item.isOrderRule &&
        !(item as AttachmentFormField)?.file &&
        Object.keys(item).length === 4
    )
    .map((item) => ({
      id: item.id,
      name: item.name,
    }));

  return (
    <Box
      borderRadius={"8px"}
      border={"0.5px solid var(--Pathrade-Gray-Groomy, #8F8F8C)"}
      background={"var(--Pathrade-White, #FFF)"}
      boxShadow={"0px 2px 4px 0px rgba(0, 0, 0, 0.10)"}
      padding={"8px 16px"}
    >
      <Flex flexDirection={"column"} gap={"8px"}>
        {/*<Heading as={"h3"} fontSize={"16px"}>*/}
        {/*  {lineSheetSet.name}*/}
        {/*</Heading>*/}
        <Flex alignItems={"flex-start"} gap={"8px"}>
          <Box flex={1} minWidth={0}>
            <Field name={"name"}>
              {(
                props: FieldProps<
                  LineSheetSetFormFields["name"],
                  LineSheetSetFormFields
                >
              ) => {
                return isEditing ? (
                  // <Input
                  //   fontSize={"16px"}
                  //   {...props.field}
                  //   padding={"0 4px"}
                  //   minHeight={"0"}
                  //   height={"auto"}
                  // />
                  <AppInput
                    value={props.field.value}
                    fontSize={"16px"}
                    placeholder={t("common:title_placeholder")}
                    onChange={(value) => {
                      props.form.setFieldValue(props.field.name, value);
                    }}
                  />
                ) : (
                  <Heading as={"h3"} fontSize={"16px"}>
                    {formikProps.values.id && (
                      <Text as={"span"}>#{formikProps.values.id} </Text>
                    )}
                    {props.field.value}
                  </Heading>
                );
              }}
            </Field>
          </Box>
          {headerAction}
        </Flex>

        {formikProps.values.orderSheetSet &&
          formikProps.values.orderSheetSet.status !== "INITIAL_DRAFT" && (
            <>
              <Box
                height={"0.5px"}
                width={"100%"}
                backgroundColor={"#8F8F8C"}
              />
              <Grid
                gridTemplateColumns={"1fr minmax(auto, 240px)"}
                // gridTemplateColumns={"200px 1fr"}
                alignItems={"center"}
                fontSize={"12px"}
                gap={"4px"}
              >
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {tTitle("common:buyer")}
                </Text>

                <Text fontSize={"12px"}>
                  {formikProps.values.orderSheetSet?.createdByCompany.name}
                </Text>
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {tTitle("common:order_id")}
                </Text>

                <Text fontSize={"12px"}>
                  {formikProps.values.orderSheetSet?.id}
                </Text>
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {tTitle("common:ordered_on")}
                </Text>

                <Text fontSize={"12px"}>
                  {tLocalDateString(
                    formikProps.values.orderSheetSet?.submittedAt
                  )}
                </Text>
                <Text fontWeight={"700"}>
                  {tTitle("common:total_ordered_amount_without_condition")}
                </Text>
                <Text>
                  {tCurrencyString(
                    formikProps.values.orderSheetSet
                      .orderedAmountWithoutCondition,
                    {
                      type: "SYMBOL",
                      code: formikProps.values.currency,
                    }
                  )}
                </Text>
                <Text fontWeight={"700"}>
                  {tTitle("common:total_ordered_amount")}
                </Text>
                <Text>
                  {tConditionAppliedCurrencyString(
                    formikProps.values.orderSheetSet.orderedConditionType,
                    formikProps.values.orderSheetSet.orderedAmount,
                    {
                      type: "SYMBOL",
                      code: formikProps.values.currency,
                    }
                  )}
                </Text>
                <Text fontWeight={"700"}>
                  {tTitle("common:total_ordered_quantity")}
                </Text>
                <Text>
                  {tNumberString(
                    formikProps.values.orderSheetSet.orderedQuantity
                  )}
                </Text>
                {formikProps.values.orderSheetSet.confirmedAmount > 0 && (
                  <>
                    <Text fontWeight={"700"}>
                      {tTitle(
                        "common:total_confirmed_amount_without_condition"
                      )}
                    </Text>
                    <Text>
                      {tCurrencyString(
                        formikProps.values.orderSheetSet
                          .confirmedAmountWithoutCondition,
                        {
                          type: "SYMBOL",
                          code: formikProps.values.currency,
                        }
                      )}
                    </Text>
                    <Text fontWeight={"700"}>
                      {tTitle("common:total_confirmed_amount_condition")}
                    </Text>
                    <Text>
                      {tConditionAppliedCurrencyString(
                        formikProps.values.orderSheetSet.confirmedConditionType,
                        formikProps.values.orderSheetSet.confirmedAmount,
                        {
                          type: "SYMBOL",
                          code: formikProps.values.currency,
                        }
                      )}
                    </Text>
                    <Text fontWeight={"700"}>
                      {tTitle("common:total_confirmed_quantity")}
                    </Text>
                    <Text>
                      {tNumberString(
                        formikProps.values.orderSheetSet.confirmedQuantity
                      )}
                    </Text>
                  </>
                )}
              </Grid>
            </>
          )}

        <Box height={"0.5px"} width={"100%"} backgroundColor={"#8F8F8C"} />

        <Flex flexDirection={"column"} gap={"4px"}>
          <Grid
            gridTemplateColumns={"1fr minmax(auto, 240px)"}
            alignItems={"center"}
            gap={"4px"}
          >
            <Field name={"type"}>
              {(
                props: FieldProps<
                  LineSheetSetFormFields["type"],
                  LineSheetSetFormFields
                >
              ) => {
                return (
                  <>
                    <FormLabel
                      fontSize={"12px"}
                      padding={"0"}
                      margin={"0"}
                      fontWeight={"700"}
                    >
                      {tTitle("common:types")}
                    </FormLabel>
                    {isEditing ? (
                      <AppLineSheetTypeSelector
                        width={"100%"}
                        value={props.field.value}
                        onSelect={(type) => {
                          props.form.setFieldValue(props.field.name, type);
                        }}
                      />
                    ) : (
                      <Text fontSize={"12px"}>
                        {props.field.value
                          ? tUpper(
                              `common\:line_sheet_set.type.${toLower(
                                props.field.value
                              )}`
                            )
                          : ""}
                      </Text>
                    )}
                  </>
                );
              }}
            </Field>

            {formikProps.values.type !== "API_ORDER" && (
              <Field name={"season"}>
                {(
                  props: FieldProps<
                    LineSheetSetFormFields["season"],
                    LineSheetSetFormFields
                  >
                ) => {
                  return (
                    <>
                      <FormLabel
                        fontSize={"12px"}
                        padding={"0"}
                        margin={"0"}
                        fontWeight={"700"}
                      >
                        {tTitle("common:season")}
                      </FormLabel>
                      {isEditing ? (
                        <AppSeasonSelect
                          width={"100%"}
                          value={props.field.value}
                          onSelect={(season) => {
                            props.form.setFieldValue(props.field.name, season);
                          }}
                        />
                      ) : (
                        <Text fontSize={"12px"}>{props.field.value}</Text>
                      )}
                    </>
                  );
                }}
              </Field>
            )}

            <Field name={"brands"}>
              {(
                props: FieldProps<
                  LineSheetSetFormFields["brands"],
                  LineSheetSetFormFields
                >
              ) => {
                return (
                  <>
                    <FormLabel
                      fontSize={"12px"}
                      padding={"0"}
                      margin={"0"}
                      fontWeight={"700"}
                    >
                      {tTitle("brand")}
                    </FormLabel>
                    <Text
                      fontSize={"12px"}
                      maxHeight={"80px"}
                      overflowY={"auto"}
                    >
                      {props.field.value
                        ?.map((brand) => brand.name)
                        .join(", ") ?? ""}
                    </Text>
                  </>
                );
              }}
            </Field>

            {identity?.company?.type !== "BOUTIQUE" && (
              <Field name={"issuedByBoutique"}>
                {(
                  props: FieldProps<
                    LineSheetSetFormFields["issuedByBoutique"],
                    LineSheetSetFormFields
                  >
                ) => {
                  return (
                    <>
                      <FormLabel
                        fontSize={"12px"}
                        padding={"0"}
                        margin={"0"}
                        fontWeight={"700"}
                      >
                        {formikProps.values.type !== "API_ORDER"
                          ? tTitle("issued_by")
                          : "발행 바이어"}
                      </FormLabel>
                      {isEditing ? (
                        <AppCompanySelect
                          types={["BOUTIQUE"]}
                          name={tTitle(
                            "line_sheet_sets:upload_line_sheet_sets_form.field.issued_by_boutique"
                          )}
                          id={props.field.value?.id}
                          width={"100%"}
                          onSelect={(company) => {
                            const value = company
                              ? { id: company.id }
                              : undefined;
                            props.form.setFieldValue(props.field.name, value);
                          }}
                        />
                      ) : (
                        <Text fontSize={"12px"}>
                          {props.field.value?.name ?? ""}
                          {identity?.company?.type === "AGENCY" &&
                            (props.field.value?.orgName
                              ? `(${props.field.value.orgName})`
                              : "")}
                        </Text>
                      )}
                    </>
                  );
                }}
              </Field>
            )}

            {/* 제출처 — 주문(오더시트세트) 단위 값. 정책상 주문 하나의 제출처는 하나라
                시트 단위가 아니라 세트 단위로 바꾼다(에이전시만 수정 가능). */}
            {isAgency && formikProps.values.orderSheetSet && (
              <>
                <FormLabel
                  fontSize={"12px"}
                  padding={"0"}
                  margin={"0"}
                  fontWeight={"700"}
                >
                  {tTitle("submit_to")}
                </FormLabel>
                <ToBeSubmittedInlineEdit
                  orderSheetSetId={formikProps.values.orderSheetSet.id}
                  value={formikProps.values.orderSheetSet.toBeSubmitted ?? null}
                  onSaved={(next) => {
                    formikProps.setFieldValue("orderSheetSet", {
                      ...formikProps.values.orderSheetSet,
                      toBeSubmitted: next,
                    });
                  }}
                  onUpdated={onOrderSheetSetUpdated}
                />
              </>
            )}
          </Grid>

          {formikProps.values.type !== "API_ORDER" && (
            <Box
              height={"0.5px"}
              width={"100%"}
              backgroundColor={"#8F8F8C"}
              marginY={"8px"}
            />
          )}

          {formikProps.values.type !== "API_ORDER" && (
            <Grid
              gridTemplateColumns={"1fr minmax(auto, 240px)"}
              alignItems={"center"}
              gap={"4px"}
            >
              {/* 수수료는 우노(에이전시)가 정한다 — 부티크에게는 보이지 않는다 */}
              {!isBoutique && (
                <Field name={"commission"}>
                  {(
                    props: FieldProps<
                      LineSheetSetFormFields["commission"],
                      LineSheetSetFormFields
                    >
                  ) => {
                    return (
                      <>
                        <FormLabel
                          fontSize={"12px"}
                          padding={"0"}
                          margin={"0"}
                          fontWeight={"700"}
                        >
                          {tTitle("commission")}
                        </FormLabel>
                        {isEditing ? (
                          <Flex alignItems={"center"} gap={"8px"}>
                            <AppInput
                              type={"number"}
                              width={"100%"}
                              rightText={"%"}
                              value={
                                props.field.value != null
                                  ? String(props.field.value)
                                  : ""
                              }
                              onChange={(value) => {
                                props.form.setFieldValue(
                                  props.field.name,
                                  value !== undefined && value !== ""
                                    ? parseFloat(value)
                                    : null
                                );
                              }}
                            />
                            <Select
                              size={"sm"}
                              width={"auto"}
                              minWidth={"120px"}
                              value={
                                formikProps.values.commissionType ?? "ORDER"
                              }
                              onChange={(e) =>
                                props.form.setFieldValue(
                                  "commissionType",
                                  e.target.value
                                )
                              }
                            >
                              <option value={"PRODUCT"}>
                                {t("commission_type_product")}
                              </option>
                              <option value={"ORDER"}>
                                {t("commission_type_order")}
                              </option>
                            </Select>
                          </Flex>
                        ) : isAgency && formikProps.values.id ? (
                          // 발행 여부/전체 편집과 무관하게 수수료는 언제든지 수정 가능
                          <CommissionInlineEdit
                            lineSheetSetId={formikProps.values.id}
                            value={props.field.value}
                            commissionType={formikProps.values.commissionType}
                            onSaved={(v) =>
                              props.form.setFieldValue(props.field.name, v)
                            }
                            onTypeSaved={(ct) =>
                              props.form.setFieldValue("commissionType", ct)
                            }
                            onUpdated={onConditionApplied}
                          />
                        ) : (
                          <Text fontSize={"12px"}>
                            {props.field.value != null
                              ? `${props.field.value}% (${
                                  (formikProps.values.commissionType ??
                                    "ORDER") === "ORDER"
                                    ? t("commission_type_order")
                                    : t("commission_type_product")
                                })`
                              : ""}
                          </Text>
                        )}
                      </>
                    );
                  }}
                </Field>
              )}

              <Field name={"moq"}>
                {(
                  props: FieldProps<
                    LineSheetSetFormFields["moq"],
                    LineSheetSetFormFields
                  >
                ) => {
                  return (
                    <>
                      <FormLabel
                        fontSize={"12px"}
                        padding={"0"}
                        margin={"0"}
                        fontWeight={"700"}
                      >
                        MOQ
                      </FormLabel>
                      {isEditing ? (
                        <AppInput
                          type={"number"}
                          width={"100%"}
                          value={
                            props.field.value != null
                              ? String(props.field.value)
                              : ""
                          }
                          onChange={(value) => {
                            props.form.setFieldValue(
                              props.field.name,
                              value !== undefined && value !== ""
                                ? parseInt(value, 10)
                                : null
                            );
                          }}
                        />
                      ) : isAgency && formikProps.values.id ? (
                        // 발행 여부/전체 편집과 무관하게 MOQ는 언제든지 수정 가능
                        <MoqInlineEdit
                          lineSheetSetId={formikProps.values.id}
                          value={props.field.value}
                          currency={formikProps.values.currency}
                          onSaved={(v) =>
                            props.form.setFieldValue(props.field.name, v)
                          }
                          onUpdated={onConditionApplied}
                        />
                      ) : (
                        <Text fontSize={"12px"}>
                          {props.field.value != null
                            ? tCurrencyString(props.field.value, {
                                type: "SYMBOL",
                                code: formikProps.values.currency ?? undefined,
                              })
                            : ""}
                        </Text>
                      )}
                    </>
                  );
                }}
              </Field>

              <Field name={"applyIva"}>
                {(
                  props: FieldProps<
                    LineSheetSetFormFields["applyIva"],
                    LineSheetSetFormFields
                  >
                ) => {
                  const selectValue =
                    props.field.value === true
                      ? "true"
                      : props.field.value === false
                      ? "false"
                      : undefined;
                  return (
                    <>
                      <FormLabel
                        fontSize={"12px"}
                        padding={"0"}
                        margin={"0"}
                        fontWeight={"700"}
                      >
                        {tTitle("apply_iva")}
                      </FormLabel>
                      {isEditing ? (
                        <AppSelect<string>
                          name={tTitle("apply_iva")}
                          width={"100%"}
                          isSearchable={false}
                          isMultiple={false}
                          isClearable={false}
                          options={[
                            { name: tTitle("apply"), value: "true" },
                            { name: tTitle("not_apply"), value: "false" },
                          ]}
                          value={selectValue}
                          onSelect={(value) => {
                            props.form.setFieldValue(
                              props.field.name,
                              value === "true"
                                ? true
                                : value === "false"
                                ? false
                                : null
                            );
                          }}
                        />
                      ) : (
                        <Text fontSize={"12px"}>
                          {props.field.value === true
                            ? tTitle("apply")
                            : props.field.value === false
                            ? tTitle("not_apply")
                            : ""}
                        </Text>
                      )}
                    </>
                  );
                }}
              </Field>

              <Field name={"currency"}>
                {(
                  props: FieldProps<
                    LineSheetSetFormFields["currency"],
                    LineSheetSetFormFields
                  >
                ) => {
                  return (
                    <>
                      <FormLabel
                        fontSize={"12px"}
                        padding={"0"}
                        margin={"0"}
                        fontWeight={"700"}
                      >
                        {tTitle("currency")}
                      </FormLabel>
                      {isEditing ? (
                        <AppCurrencySelect
                          width={"100%"}
                          value={props.field.value}
                          onSelect={(currency) => {
                            props.form.setFieldValue(
                              props.field.name,
                              currency
                            );
                          }}
                        />
                      ) : (
                        <Text fontSize={"12px"}>{props.field.value}</Text>
                      )}
                    </>
                  );
                }}
              </Field>

              <Field name={"validUntil"}>
                {(
                  props: FieldProps<Date | undefined, LineSheetSetFormFields>
                ) => {
                  return (
                    <>
                      <FormLabel
                        fontSize={"12px"}
                        padding={"0"}
                        margin={"0"}
                        fontWeight={"700"}
                      >
                        {tTitle("order_valid_until")}
                      </FormLabel>
                      {isEditing ? (
                        <CustomDatePicker
                          name={tTitle("order_valid_until")}
                          value={props.field.value}
                          showTimeInput={true}
                          onChange={(value) =>
                            props.form.setFieldValue(props.field.name, value)
                          }
                          onValidityChange={(isValid) =>
                            props.form.setStatus({
                              ...(props.form.status ?? {}),
                              hasInvalidInput: !isValid,
                            })
                          }
                          isClearable={false}
                        />
                      ) : (
                        <Text fontSize={"12px"}>
                          {tLocalDateTimeString2(props.field.value)}
                        </Text>
                      )}
                    </>
                  );
                }}
              </Field>

              {formikProps.values.id !== -1 && !isEditing && (
                <>
                  <Text fontSize={"12px"} fontWeight={"700"}>
                    {tTitle("common:published_on")}
                  </Text>

                  <Text fontSize={"12px"}>
                    {tLocalDateString(formikProps.values.publishedAt)}
                  </Text>

                  {identity?.company?.type === "AGENCY" && (
                    <>
                      <Text fontSize={"12px"} fontWeight={"700"}>
                        {tTitle("common:published_to")}
                      </Text>

                      <Text
                        fontSize={"12px"}
                        fontWeight={"700"}
                        decoration={"underline"}
                        as={"a"}
                        href={"#"}
                        onClick={(event) => {
                          event.preventDefault();
                          onPublishClick();
                        }}
                      >
                        {issuedToBuyersMeta}
                      </Text>
                    </>
                  )}
                </>
              )}
            </Grid>
          )}
        </Flex>

        {formikProps.values.type !== "API_ORDER" &&
          identity?.company?.type !== "BUYER" &&
          (isEditing || formikProps.values.externalFileLinks.length > 0) && (
            <Field name={"externalFileLinks"}>
              {(
                props: FieldProps<
                  LineSheetSetFormFields["externalFileLinks"],
                  LineSheetSetFormFields
                >
              ) => {
                return (
                  <>
                    <Box
                      height={"0.5px"}
                      width={"100%"}
                      backgroundColor={"#8F8F8C"}
                    />
                    <Flex
                      flexDirection={"column"}
                      gap={"4px"}
                      fontSize={"12px"}
                    >
                      <Text fontSize={"12px"}>
                        {tTitle("external_file_links")}
                      </Text>
                      <AppListFormControl
                        value={props.field.value || []}
                        isDisabled={!isEditing || props.form.values.id !== -1}
                        onChange={(value) => {
                          formikProps.setFieldValue(props.field.name, value);
                        }}
                        isLiteral={!isEditing}
                      />
                    </Flex>
                  </>
                );
              }}
            </Field>
          )}

        {formikProps.values.type !== "API_ORDER" &&
          attachmentFiles.length > 0 && (
            <>
              <Box
                height={"0.5px"}
                width={"100%"}
                backgroundColor={"#8F8F8C"}
              />
              <Flex flexDirection={"column"} gap={"4px"} fontSize={"12px"}>
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {tTitle("attachment")}
                </Text>
                {attachmentFiles.map((file, index) => {
                  const fileType =
                    file.name.toLowerCase().split(".").at(1)?.toLowerCase() ??
                    "default";
                  return (
                    <Flex
                      key={index}
                      alignItems={"center"}
                      gap={"8px"}
                      padding={"8px"}
                      borderRadius={"4px"}
                      border={"0.5px solid #D4D4D1"}
                      background={"#FCFCFE"}
                      marginBottom={"8px"}
                    >
                      <Box flexShrink={0}>
                        <SVG
                          src={
                            fileIconsByType[fileType] ?? fileIconsByType.default
                          }
                          width={"20px"}
                          height={"20px"}
                        />
                      </Box>
                      <Text
                        textStyle={"body-sm-bold"}
                        color={"#444444"}
                        flexGrow={1}
                        overflow={"hidden"}
                        textOverflow={"ellipsis"}
                        whiteSpace={"nowrap"}
                      >
                        {file.name}
                      </Text>
                      <Box
                        flexShrink={0}
                        cursor={"pointer"}
                        onClick={() => {
                          fireDownloadUnoFile(
                            {
                              unoFileId: file.id,
                            },
                            {}
                          );
                        }}
                      >
                        <SVG
                          src={"/icons-v1_1/download.svg"}
                          width={"16px"}
                          height={"16px"}
                        />
                      </Box>
                    </Flex>
                  );
                })}
              </Flex>
            </>
          )}

        {formikProps.values.type !== "API_ORDER" && (
          <>
            <Box height={"0.5px"} width={"100%"} backgroundColor={"#8F8F8C"} />
            <Flex flexDirection={"column"} gap={"4px"}>
              <Flex alignItems={"center"} gap={"8px"}>
                <Text fontSize={"12px"} fontWeight={"700"}>
                  {tTitle("common:order_rules")}
                </Text>
                <OrderRuleStatusLabel
                  status={formikProps.values.orderRuleStatus}
                  isHidden={formikProps.values.type === "SEASON"}
                  fallbackAppliedAt={
                    formikProps.values.applyConditionCompletedAt
                  }
                  fallbackHasOrderRule={
                    (formikProps.values.joinedOrderRules ?? "").trim() !== ""
                  }
                />
                <Spacer />
                {canEditOrderRuleInline && !isInlineOrderRuleEditing && (
                  <AppButton
                    variant={"normal"}
                    onClick={() => setIsInlineOrderRuleEditing(true)}
                    chakraProps={{ p: "4px 8px" }}
                  >
                    <Text textStyle="body-sm-bold">{tTitle("edit")}</Text>
                  </AppButton>
                )}
                {canEditOrderRuleInline && isInlineOrderRuleEditing && (
                  <AppButton
                    variant={"normal"}
                    isDisabled={isUpdateOrderRulesLoading}
                    onClick={() => {
                      // 고친 내용을 버리고 저장된 오더룰로 되돌린다
                      setEditedOrderRules(null);
                      setIsInlineOrderRuleEditing(false);
                    }}
                    chakraProps={{ p: "4px 8px" }}
                  >
                    <Text textStyle="body-sm-bold">{tTitle("cancel")}</Text>
                  </AppButton>
                )}
                {showOrderRuleSaveButton && (
                  <AppButton
                    variant={"primary"}
                    isDisabled={!isOrderRuleDirty}
                    isLoading={isUpdateOrderRulesLoading}
                    onClick={handleSaveOrderRules}
                    chakraProps={{ p: "4px 8px" }}
                  >
                    <Text textStyle="body-sm-bold">
                      {tTitle("save_order_rules")}
                    </Text>
                  </AppButton>
                )}
                {isAgency && formikProps.values.id && (
                  <AppButton
                    variant={"normal"}
                    onClick={() => setIsApplyConditionOpen(true)}
                    chakraProps={{ p: "4px 8px" }}
                  >
                    <Text textStyle="body-sm-bold">
                      {tTitle("apply_order_rule")}
                    </Text>
                  </AppButton>
                )}
              </Flex>
              {orderRuleFiles && orderRuleFiles.length > 0 && (
                <Flex flexDirection={"column"} gap={"4px"} margin={"8px 0px"}>
                  {orderRuleFiles.map((file, index) => {
                    const fileType =
                      file.name.toLowerCase().split(".").at(1)?.toLowerCase() ??
                      "default";
                    return (
                      <Flex
                        key={index}
                        alignItems={"center"}
                        gap={"8px"}
                        padding={"8px"}
                        borderRadius={"4px"}
                        border={"0.5px solid #D4D4D1"}
                        background={"#FCFCFE"}
                      >
                        <Box flexShrink={0}>
                          <SVG
                            src={
                              fileIconsByType[fileType] ??
                              fileIconsByType.default
                            }
                            width={"20px"}
                            height={"20px"}
                          />
                        </Box>
                        <Text
                          textStyle={"body-sm-bold"}
                          color={"#444444"}
                          flexGrow={1}
                          overflow={"hidden"}
                          textOverflow={"ellipsis"}
                          whiteSpace={"nowrap"}
                        >
                          {file.name}
                        </Text>
                        <Box
                          flexShrink={0}
                          cursor={"pointer"}
                          onClick={() => {
                            fireDownloadUnoFile(
                              {
                                unoFileId: file.id,
                              },
                              {}
                            );
                          }}
                        >
                          <SVG
                            src={"/icons-v1_1/download.svg"}
                            width={"16px"}
                            height={"16px"}
                          />
                        </Box>
                      </Flex>
                    );
                  })}
                </Flex>
              )}
              {isAgency && formikProps.values.id && (
                <OrderRuleDraftBox
                  lineSheetSetId={formikProps.values.id}
                  isEditing={canEditOrderRule}
                  onApply={handleChangeOrderRules}
                  onSave={
                    canSaveOrderRuleDirectly
                      ? handleSaveOrderRuleDraft
                      : undefined
                  }
                  isSaving={isUpdateOrderRulesLoading}
                  appliedText={orderRuleValue}
                />
              )}
              <TextareaAutosize
                value={orderRuleValue}
                style={{
                  fontSize: "12px",
                  borderRadius: "4px",
                  border: canEditOrderRule ? "0.5px solid #8F8F8C" : "none",
                  background: "#FFF",
                  padding: canEditOrderRule ? "0 4px" : "0",
                }}
                readOnly={!canEditOrderRule}
                onChange={(event) => {
                  handleChangeOrderRules(event.target.value);
                }}
                minRows={10}
                maxRows={30}
              />
            </Flex>
          </>
        )}
      </Flex>
      {isAgency && formikProps.values.id && (
        <ApplyConditionModal
          isOpen={isApplyConditionOpen}
          onClose={() => setIsApplyConditionOpen(false)}
          lineSheetSetId={formikProps.values.id}
          brands={formikProps.values.brands ?? []}
          lineSheets={lineSheetTargets}
          seasons={seasonTargets}
          orderRulesText={formikProps.values.joinedOrderRules}
          orderRuleFiles={orderRuleFiles}
          onApplied={onConditionApplied}
        />
      )}
    </Box>
  );
}
